import { type FormEvent, useState } from "react";

import { useUpdateDashboard } from "@web-app/api";
import {
  Button,
  Field,
  Input,
  Label,
  Modal,
  useAlert,
} from "@web-app/components";
import type { Dashboard } from "@web-app/types";

export function EditDashboard({
  dashboard,
  onClose,
  onDelete,
}: {
  dashboard: Dashboard;
  onClose: () => void;
  onDelete: (dashboardId: string) => Promise<void>;
}) {
  const [title, setTitle] = useState(dashboard.title);
  const [description, setDescription] = useState(dashboard.description || "");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const updateDashboardMutation = useUpdateDashboard();
  const { showAlert } = useAlert();

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      await updateDashboardMutation.mutateAsync({
        id: dashboard.id,
        payload: {
          description,
          title,
        },
      });

      showAlert({
        message: "Dashboard updated successfully",
        title: "Success",
        type: "success",
      });

      onClose();
    } catch {
      showAlert({
        message: "Failed to update dashboard. Please try again.",
        title: "Error",
        type: "error",
      });
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete(dashboard.id);
      setShowDeleteModal(false);
      onClose();
    } catch {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <div className="flex flex-col h-full">
        <div className="flex-1 overflow-y-auto p-1">
          <form
            id="edit-dashboard-form"
            onSubmit={onSubmit}
            className="space-y-6"
          >
            <Field>
              <Label required>Title</Label>
              <Input
                type="text"
                name="title"
                value={title}
                required
                placeholder="e.g., Revenue Overview"
                onChange={(value) => setTitle(value)}
              />
            </Field>
            <Field>
              <Label>Description</Label>
              <Input
                type="text"
                name="description"
                value={description}
                placeholder="e.g., Track daily, weekly and monthly revenue trends"
                onChange={(value) => setDescription(value)}
              />
            </Field>
          </form>
        </div>
        <div className="absolute bottom-0 left-0 w-full border-t border-gray-200 p-4">
          <div className="flex justify-between">
            <div className="flex gap-3">
              <Button type="submit" form="edit-dashboard-form">
                Update
              </Button>
              <Button type="button" plain onClick={onClose}>
                Cancel
              </Button>
            </div>
            <Button
              color="red"
              onClick={() => setShowDeleteModal(true)}
              disabled={isDeleting}
            >
              Delete
            </Button>
          </div>
        </div>
      </div>

      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Dashboard"
        subtitle="Are you sure you want to delete this dashboard? This action cannot be undone."
        width={400}
      >
        <div className="mt-6 flex justify-end gap-3">
          <Button plain onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button color="red" onClick={handleDelete} isLoading={isDeleting}>
            Delete
          </Button>
        </div>
      </Modal>
    </>
  );
}
